import { Link } from "@remix-run/react";

import type { ReactNode } from "react";

/**
 * Shared shell for legal pages (Términos, Privacidad) — a long-form paper sheet in
 * the Cuaderno Vivo system: Fraunces headings, readable measure, ruled-paper card.
 */
export function LegalPage({
  eyebrow,
  title,
  updatedAt,
  children,
}: {
  eyebrow: string;
  title: string;
  updatedAt: string;
  children: ReactNode;
}) {
  return (
    <section className="landing-grain relative bg-landing-cream/70 px-5 py-16 backdrop-blur-[1px] sm:px-8 sm:py-20">
      <div className="relative mx-auto max-w-[780px]">
        <div className="text-center">
          <span className="landing-eyebrow block font-bold text-landing-red">{eyebrow}</span>
          <h1 className="font-display mt-3 text-[clamp(26px,3.6vw,38px)] font-extrabold leading-tight tracking-tight text-landing-text">
            {title}
          </h1>
          <p className="landing-meta mt-2 uppercase tracking-[0.12em] text-landing-muted">
            Última actualización: {updatedAt}
          </p>
        </div>
        <article className="legal-body relative mt-8 rounded-[4px] bg-landing-paper p-7 shadow-landing-lg sm:p-10">
          {children}
        </article>
      </div>
    </section>
  );
}

export function LegalSection({
  title,
  id,
  children,
}: {
  title: string;
  /** Ancla para linkear directo a la sección (p. ej. `/privacy#cookies` desde el aviso). */
  id?: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="mb-7 scroll-mt-24 last:mb-0">
      <h2 className="font-display mb-2 text-[19px] font-bold text-landing-text">{title}</h2>
      <div className="flex flex-col gap-2.5 text-[14.5px] leading-relaxed text-landing-text/90 [&_li]:ml-5 [&_li]:list-disc [&_ul]:flex [&_ul]:flex-col [&_ul]:gap-1.5">
        {children}
      </div>
    </section>
  );
}

/**
 * Link de un texto legal a otra página del sitio. En la página es navegación normal; dentro de
 * un diálogo (`newTab`) abre una pestaña aparte para no sacar a la persona del formulario.
 */
export function LegalLink({
  to,
  newTab = false,
  children,
}: {
  to: string;
  newTab?: boolean;
  children: ReactNode;
}) {
  const className = "font-bold text-landing-red";
  if (newTab) {
    return (
      <a className={className} href={to} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }
  return (
    <Link className={className} to={to}>
      {children}
    </Link>
  );
}
